import { Suspense, useEffect, useRef, type ReactNode } from 'react';
import { useNavigationType } from 'react-router-dom';

import SkeletonScreen, { SkeletonLine } from '@/components/Skeleton';
import { ListSkeleton } from '@/components/PageSkeletons';

/** Runs only after the incoming chunk commits, never for a loading placeholder. */
function ScreenReady({ children }: { children: ReactNode }) {
  const navigation = useRef(useNavigationType()).current;
  useEffect(() => {
    if (navigation === 'POP') return; // Native history restoration owns Back/Forward.
    // Wait for the departing dialogs' passive cleanup and the new screen's focus
    // effects. Do not steal focus from an input or a newly opened modal.
    const frame = requestAnimationFrame(() => {
      const main = document.getElementById('main');
      if (!main) return;
      const active = document.activeElement;
      const dialog = document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]');
      if (!dialog) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      if (!dialog && !(active && active !== main && main.contains(active))) {
        main.focus({ preventScroll: true });
      }
      // Only explicitly marked, overlay-free content may fade. A view/main
      // ancestor would change the stacking of fixed sheets during its animation.
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        main.querySelectorAll<HTMLElement>('[data-screen-arrival]').forEach((node) => {
          node.classList.add('screen-arrival');
        });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [navigation]);
  return <>{children}</>;
}

/** Keyed by the matched screen in App, not the URL/query or history entry. */
export default function RouteScreen({
  children,
  loading,
}: {
  children: ReactNode;
  loading?: ReactNode;
}) {
  return (
    <Suspense
      fallback={
        <div className="view view--loading">
          <SkeletonScreen>
            {loading ?? (
              <>
                <SkeletonLine width="55%" height="1.5rem" />
                <ListSkeleton rows={4} />
              </>
            )}
          </SkeletonScreen>
        </div>
      }
    >
      <ScreenReady>{children}</ScreenReady>
    </Suspense>
  );
}
